import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Grid, List, ChevronDown, ExternalLink, Star } from 'lucide-react';
import { products, productsByCategory, featuredProducts } from '../../data/products';
import { Product } from '../../types';
import OptimizedImage from '../../components/Image/OptimizedImage';
import ProductCard from '../../components/ProductCard/ProductCard';
import { emailUtils } from '../../utils/contactUtils';

const Products: React.FC = () => {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<'all' | 'software' | 'hardware'>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [sortBy, setSortBy] = useState<'name' | 'category'>('name');
  // const [showFilters, setShowFilters] = useState(false);

  // 过滤和搜索产品
  const filteredProducts = useMemo(() => {
    let filtered = selectedCategory === 'all' ? products : productsByCategory[selectedCategory];
    
    if (searchTerm) {
      filtered = filtered.filter(product =>
        product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        product.features.some(feature => feature.toLowerCase().includes(searchTerm.toLowerCase()))
      );
    }

    // 排序
    return filtered.sort((a, b) => {
      if (sortBy === 'name') {
        return a.name.localeCompare(b.name);
      } else {
        return a.category.localeCompare(b.category);
      }
    });
  }, [searchTerm, selectedCategory, sortBy]);

  // 产品统计
  const productStats = {
    total: products.length,
    software: productsByCategory.software.length,
    hardware: productsByCategory.hardware.length
  };

  const CategoryFilter = () => (
    <div className="flex flex-wrap gap-2">
      {[
        { key: 'all', label: `${t('pages.products.categories.all')} (${productStats.total})`, count: productStats.total },
        { key: 'hardware', label: `${t('pages.products.categories.hardware')} (${productStats.hardware})`, count: productStats.hardware },
        { key: 'software', label: `${t('pages.products.categories.software')} (${productStats.software})`, count: productStats.software }
      ].map(({ key, label }) => (
        <button
          key={key}
          onClick={() => setSelectedCategory(key as any)}
          className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 ${
            selectedCategory === key
              ? 'bg-blue-600 text-white shadow-lg'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
          }`}
        >
          {label}
        </button>
      ))}
    </div>
  );

  // 获取翻译的产品信息的函数
  const getTranslatedProductInfo = (productId: string) => {
    const productTranslations = t(`products.${productId}`, { returnObjects: true }) as any
    if (productTranslations && typeof productTranslations === 'object') {
      return productTranslations
    }
    // 如果没有翻译，返回空对象
    return {}
  }

  const ProductListView = ({ product }: { product: Product }) => {
    const translatedProduct = getTranslatedProductInfo(product.id)
    
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow duration-200">
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="w-full lg:w-64 h-64 flex-shrink-0">
            <OptimizedImage
              src={product.image || '/images/placeholder.jpg'}
              alt={translatedProduct.name || product.name}
              className="w-full h-full object-contain rounded-lg bg-gray-50"
            />
          </div>
          
          <div className="flex-1">
            <div className="flex justify-between items-start mb-3">
              <div>
                <span className={`inline-block px-3 py-1 rounded-full text-xs font-medium mb-2 ${
                  product.category === 'hardware' 
                    ? 'bg-blue-100 text-blue-800' 
                    : 'bg-green-100 text-green-800'
                }`}>
                  {product.category === 'hardware' ? t('pages.products.categories.hardware') : t('pages.products.categories.software')}
                </span>
                <h3 className="text-xl font-bold text-gray-900">{translatedProduct.name || product.name}</h3>
              </div>
              {featuredProducts.some(fp => fp.id === product.id) && (
                <Star className="text-yellow-500 fill-current" size={20} />
              )}
            </div>
            
            <p className="text-gray-600 mb-4 line-clamp-2">{translatedProduct.description || product.description}</p>
            
            <div className="mb-4">
              <h4 className="font-semibold text-gray-800 mb-2">{t('pages.products.actions.keyFeatures')}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-1">
                {(translatedProduct.features || product.features).slice(0, 6).map((feature: string, idx: number) => (
                  <div key={idx} className="flex items-center text-sm text-gray-600">
                    <span className="text-green-500 mr-2">✓</span>
                    {feature}
                  </div>
                ))}
              </div>
            </div>
            
            <div className="flex justify-center">
              <button 
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors duration-200 flex items-center gap-2"
                onClick={() => emailUtils.openGeneralEmail(t, `Product Inquiry - ${translatedProduct.name || product.name}`)}
              >
                <ExternalLink size={16} />
                {t('pages.products.actions.learnMore')}
              </button>
            </div>
          </div>
        </div>
      </div>
    )
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-4xl lg:text-6xl font-bold mb-6">
            {t('pages.products.hero.title')}
          </h1>
          <p className="text-xl lg:text-2xl mb-8 max-w-3xl mx-auto opacity-90">
            {t('pages.products.hero.subtitle')}
          </p>
          <div className="flex justify-center">
            <button 
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              onClick={() => emailUtils.openConsultationEmail(t)}
            >
              {t('pages.products.hero.cta')}
            </button>
          </div>
        </div>
      </section>

      {/* Featured Products Banner */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-4">
              {t('pages.products.featured.title')}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {t('pages.products.featured.subtitle')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featuredProducts.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          {/* Search and Filters Header */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-8">
            <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
              {/* Search Bar */}
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder={t('pages.products.search.placeholder')}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {/* Filter Controls */}
              <div className="flex items-center gap-4">
                {/* Sort Dropdown */}
                <div className="relative">
                  <select
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value as 'name' | 'category')}
                    className="appearance-none bg-white border border-gray-300 rounded-lg px-4 py-2 pr-8 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="name">{t('pages.products.search.sortByName')}</option>
                    <option value="category">{t('pages.products.search.sortByCategory')}</option>
                  </select>
                  <ChevronDown className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
                </div>

                {/* View Mode Toggle */}
                <div className="flex border border-gray-300 rounded-lg">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`p-2 ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    <Grid size={20} />
                  </button>
                  <button
                    onClick={() => setViewMode('list')}
                    className={`p-2 ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-100'}`}
                  >
                    <List size={20} />
                  </button>
                </div>
              </div>
            </div>

            {/* Category Filters */}
            <div className="mt-6">
              <CategoryFilter />
            </div>
          </div>

          {/* Results Info */}
          <div className="flex justify-between items-center mb-6">
            <h3 className="text-lg font-semibold text-gray-900">
              {filteredProducts.length} {t('pages.products.search.resultsFound')}
            </h3>
            {searchTerm && (
              <button
                onClick={() => setSearchTerm('')}
                className="text-blue-600 hover:text-blue-700 font-medium"
              >
                {t('pages.products.search.clearSearch')}
              </button>
            )}
          </div>

          {/* Products Grid/List */}
          {filteredProducts.length > 0 ? (
            <div className={
              viewMode === 'grid' 
                ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" 
                : "space-y-6"
            }>
              {filteredProducts.map((product) => (
                viewMode === 'grid' ? (
                  <ProductCard key={product.id} product={product} />
                ) : (
                  <ProductListView key={product.id} product={product} />
                )
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="text-gray-400 mb-4">
                <Search size={48} className="mx-auto" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('pages.products.search.noProductsFound')}</h3>
              <p className="text-gray-600">{t('pages.products.search.tryAdjusting')}</p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold mb-6">
            {t('pages.products.cta.title')}
          </h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto opacity-90">
            {t('pages.products.cta.description')}
          </p>
          <div className="flex justify-center">
            <button 
              className="bg-white text-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors duration-200"
              onClick={() => emailUtils.openConsultationEmail(t)}
            >
              {t('pages.products.cta.requestQuote')}
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products; 